'use client';

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import FileUpload from './FileUpload';
import DataQuality from './DataQuality';
import BrandLogo from './BrandLogo';

type TabType = 'overview' | 'analytics'
  | 'postoffice-table' | 'postoffice-charts' | 'upload' | 'data-quality';

type SectionKey = 'postoffice';

const PostOfficeDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('overview');

  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    postoffice: true,
  });

  const toggleSection = (section: SectionKey) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const tabs = [
    {
      id: 'overview' as TabType,
      name: 'Overview',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      ),
      badge: null,
    },
    {
      id: 'analytics' as TabType,
      name: 'Analytics',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      badge: null,
    },
    {
      id: 'data-quality' as TabType,
      name: 'Data Quality',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      badge: null,
    },
    {
      id: 'upload' as TabType,
      name: 'Upload Data',
      icon: (
        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      badge: null,
    },
  ];

  const collapsibleSections: {
    key: SectionKey;
    label: string;
    color: string;
    iconPath: string;
    subTabs: { id: TabType; label: string; iconPath: string }[];
  }[] = [
      {
        key: 'postoffice',
        label: 'Post Office',
        color: '#f59e0b',
        iconPath: 'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
        subTabs: [
          { id: 'postoffice-table', label: 'Table', iconPath: 'M3 10h18M3 14h18M10 6h4M10 18h4M3 6h4M3 18h4M17 6h4M17 18h4' },
          { id: 'postoffice-charts', label: 'Charts', iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
        ],
      },
    ];

  const renderUserInfo = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
      <div style={{ width: '36px', height: '36px', borderRadius: '9px', background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 700, color: 'white', flexShrink: 0 }}>
        {user?.name.charAt(0)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.name}</p>
        <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{user?.role}</p>
      </div>
    </div>
  );

  const ComingSoonContent = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div className="flex items-center justify-center min-h-[400px] p-6">
      <div className="text-center p-10 bg-card rounded-2xl border border-border shadow-sm max-w-md w-full space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto border border-amber-100 shadow-sm">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-foreground">{title}</h2>
        <p className="text-xs text-muted-foreground leading-relaxed">{subtitle}</p>
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-xl text-xs font-bold border border-amber-200">
          <span className="w-2 h-2 rounded-full bg-amber-600 animate-pulse" />
          <span>Postal Operations Module in Staging</span>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex' }}>

      {/* ══ SIDEBAR ══ */}
      <aside style={{
        width: '260px', flexShrink: 0, background: '#ffffff',
        borderRight: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column',
        height: '100vh', position: 'sticky', top: 0,
        boxShadow: '2px 0 8px rgba(0,0,0,0.04)', overflowY: 'auto', overflowX: 'hidden',
      }}>

        {/* Header */}
        <div style={{ padding: '10px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
          <div style={{ padding: '6px 4px 10px' }}>
            <BrandLogo />
          </div>
          {renderUserInfo()}
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '12px 10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>

          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '11px 14px', borderRadius: '12px', border: 'none',
                  cursor: 'pointer', fontSize: '13px', fontWeight: isActive ? 600 : 500,
                  transition: 'all 0.18s', textAlign: 'left',
                  background: isActive ? 'linear-gradient(135deg, #f59e0b, #fbbf24)' : 'transparent',
                  color: isActive ? 'white' : '#64748b',
                  boxShadow: isActive ? '0 4px 14px rgba(245,158,11,0.25)' : 'none',
                }}
                onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#0f172a'; } }}
                onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; } }}
              >
                <div style={{ flexShrink: 0 }}>{tab.icon}</div>
                <span style={{ flex: 1 }}>{tab.name}</span>
              </button>
            );
          })}

          <div style={{ margin: '10px 6px', borderTop: '1px solid #f1f5f9' }} />

          {collapsibleSections.map((section) => {
            const isExpanded = expandedSections[section.key];
            const isSectionActive = activeTab.startsWith(section.key);

            return (
              <div key={section.key}>
                <button
                  onClick={() => toggleSection(section.key)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '11px 14px', borderRadius: '12px', border: 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                    transition: 'all 0.18s', textAlign: 'left',
                    background: isSectionActive ? `${section.color}14` : isExpanded ? '#f8fafc' : 'transparent',
                    color: isSectionActive ? section.color : '#374151',
                  }}
                  onMouseEnter={(e) => { if (!isSectionActive) e.currentTarget.style.background = '#f1f5f9'; }}
                  onMouseLeave={(e) => { if (!isSectionActive) e.currentTarget.style.background = isSectionActive ? `${section.color}14` : isExpanded ? '#f8fafc' : 'transparent'; }}
                >
                  <div style={{ width: '18px', height: '18px', flexShrink: 0 }}>
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={section.iconPath} />
                    </svg>
                  </div>
                  <span style={{ flex: 1 }}>{section.label}</span>
                  <svg
                    width="13" height="13" fill="none" viewBox="0 0 24 24"
                    stroke="#9ca3af" strokeWidth={2.5}
                    style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease', flexShrink: 0 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </button>

                {isExpanded && (
                  <div style={{ paddingLeft: '14px', marginTop: '2px', marginBottom: '2px' }}>
                    <div style={{ borderLeft: `2px solid ${section.color}25`, paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '1px' }}>
                      {section.subTabs.map((sub) => {
                        const isSubActive = activeTab === sub.id;
                        return (
                          <button
                            key={sub.id}
                            onClick={() => setActiveTab(sub.id)}
                            style={{
                              width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
                              padding: '9px 12px', borderRadius: '9px', border: 'none',
                              cursor: 'pointer', fontSize: '12.5px',
                              fontWeight: isSubActive ? 600 : 400,
                              transition: 'all 0.15s', textAlign: 'left',
                              background: isSubActive ? section.color : 'transparent',
                              color: isSubActive ? '#ffffff' : '#6b7280',
                              boxShadow: isSubActive ? `0 3px 10px ${section.color}33` : 'none',
                            }}
                            onMouseEnter={(e) => { if (!isSubActive) { e.currentTarget.style.background = `${section.color}12`; e.currentTarget.style.color = section.color; } }}
                            onMouseLeave={(e) => { if (!isSubActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; } }}
                          >
                            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={isSubActive ? 2.2 : 1.8} style={{ flexShrink: 0 }}>
                              <path strokeLinecap="round" strokeLinejoin="round" d={sub.iconPath} />
                            </svg>
                            <span>{sub.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer — Logout */}
        <div style={{ padding: '14px', borderTop: '1px solid #f1f5f9', flexShrink: 0 }}>
          <button
            onClick={logout}
            title="Logout"
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '11px 16px', background: '#fef2f2', border: '1px solid #fecaca',
              color: '#ef4444', fontWeight: 600, fontSize: '13px',
              borderRadius: '12px', cursor: 'pointer', transition: 'all 0.18s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" x2="9" y1="12" y2="12" />
            </svg>
            Sign Out
          </button>
        </div>
      </aside>

      {/* ══ MAIN CONTENT ══ */}
      <main style={{ flex: 1, overflowY: 'auto' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '28px' }}>

          {activeTab === 'overview' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', margin: '0 0 4px' }}>Post Office Dashboard</h1>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Post Office analytics & insights</p>
              </div>
              <ComingSoonContent title="Overview Coming Soon" subtitle="Post Office overview data will appear here." />
            </div>
          )}

          {activeTab === 'analytics' && (
            <ComingSoonContent title="Analytics Coming Soon" subtitle="Post Office analytics will appear here." />
          )}

          {activeTab === 'postoffice-table' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '24px 32px', background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(245,158,11,0.2)' }}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18M3 6h18M3 18h18" /></svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>Post Office — Table</h2>
                  <p style={{ fontSize: '13px', color: '#d97706', margin: 0, marginTop: '2px' }}>Detailed Post Office data table</p>
                </div>
              </div>
              <div style={{ padding: '32px' }}>
                <ComingSoonContent title="Table Coming Soon" subtitle="Post Office table data will appear here." />
              </div>
            </div>
          )}

          {activeTab === 'postoffice-charts' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '24px 32px', background: 'linear-gradient(135deg, #fffbeb, #fef3c7)', borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(245,158,11,0.2)' }}>
                  <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                </div>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#1e293b', margin: 0 }}>Post Office — Charts</h2>
                  <p style={{ fontSize: '13px', color: '#d97706', margin: 0, marginTop: '2px' }}>Visual Post Office analytics</p>
                </div>
              </div>
              <div style={{ padding: '32px' }}>
                <ComingSoonContent title="Charts Coming Soon" subtitle="Post Office charts will appear here." />
              </div>
            </div>
          )}

          {activeTab === 'data-quality' && (
            <div style={{ padding: '32px' }}><DataQuality /></div>
          )}
          {activeTab === 'upload' && (
            <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '32px' }}><FileUpload onUploadComplete={() => setActiveTab('overview')} /></div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default PostOfficeDashboard;